import React, { useState } from 'react';

import { useAppDispatch } from '../hooks';

import { DocumentActions } from '../actions/document/document.actions';

import type { Document as BsmDocument } from '../interfaces/document/document.interface';

import Modal from './components/Modal';

// =============================================================================
//  Document edit modal (v3)
//
//  Edits the rename + access-scope fields surfaced by the v3 Documents page:
//
//    name          — display name (does not rename the underlying file)
//    document_type — enum (contract / info / presentation)
//    access_scope  — All staff / Directors / Advisory team / Sponsor team / Compliance
// =============================================================================

const TYPE_OPTIONS = [
  { value: 'contract', label: 'Contract' },
  { value: 'info', label: 'Info' },
  { value: 'presentation', label: 'Presentation' }
] as const;

const ACCESS_OPTIONS = [
  'All staff',
  'Directors',
  'Advisory team',
  'Sponsor team',
  'Compliance'
] as const;

interface Props {
  document: BsmDocument;
  onClose: () => void;
}

const DocumentEditModal = ({ document, onClose }: Props) => {
  const dispatch = useAppDispatch();

  const [name, setName] = useState<string>(document.name || '');
  const [documentType, setDocumentType] = useState<string>(document.document_type || 'info');
  const [accessScope, setAccessScope] = useState<string>(document.access_scope || 'All staff');
  const [saving, setSaving] = useState(false);

  const handleSave = () => {
    setSaving(true);
    dispatch(
      DocumentActions.updateDocument(
        document.id as number | string,
        {
          name,
          document_type: documentType,
          access_scope: accessScope
        } as Partial<BsmDocument> as BsmDocument,
        () => {
          setSaving(false);
          onClose();
        }
      )
    );
  };

  const footer = (
    <>
      <button type="button" className="btn ghost" onClick={onClose} disabled={saving}>
        Cancel
      </button>
      <button type="button" className="btn primary" onClick={handleSave} disabled={saving}>
        {saving ? 'Saving…' : 'Save changes'}
      </button>
    </>
  );

  return (
    <Modal title={`Edit ${document.name || 'document'}`} onClose={onClose} footer={footer}>
      <div className="field">
        <span className="label">Display name</span>
        <input
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="What this document is called in the library"
        />
      </div>

      <div className="field">
        <span className="label">Type</span>
        <select value={documentType} onChange={(e) => setDocumentType(e.target.value)}>
          {TYPE_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
      </div>

      <div className="field">
        <span className="label">Access scope</span>
        <select value={accessScope} onChange={(e) => setAccessScope(e.target.value)}>
          {ACCESS_OPTIONS.map((a) => (
            <option key={a} value={a}>
              {a}
            </option>
          ))}
        </select>
      </div>

      <p className="muted" style={{ fontSize: 12, lineHeight: 1.4, marginTop: 4, marginBottom: 0 }}>
        Replacing the actual file attachment still uses the legacy form under
        <code> /admin/documents/{document.id}</code>.
      </p>
    </Modal>
  );
};

export default DocumentEditModal;
